import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import WarningCircleIcon from '@/assets/icons/warning-circle.svg?react'
import Table, { type Column, type RowSelection } from '@/components/Table/Table'
import { TABLE_CELL_WIDTH } from '@/components/Table/table-cell-width'
import Tag from '@/components/Tag/Tag'
import { useNumberColumn } from '@/hooks/useColumns'
import type useRowSelection from '@/hooks/useRowSelection'
import { useScreenResolution } from '@/hooks/useScreenResolution'
import { formatDate } from '@/utils/format-date'
import { getColumnWidth } from '@/utils/get-column-width'
import { timeWithoutSeconds } from '@/utils/time-without-seconds'

import styles from './AbsencePlanTable.module.scss'
import { getAbsencesStatusTag } from '../constants/get-absences-status-tags'
import type { StudentAttendance } from '../queries/useStudentAttendance'
import type { AbsencesPlanFormPayload } from '../utils/create-absence-plan-schema'

type AbsencePlanTableProps = {
  data: StudentAttendance[]
  count: number
  isLoading: boolean
  rowSelection: ReturnType<typeof useRowSelection>
}

const AbsencePlanTable = (props: AbsencePlanTableProps) => {
  const { t } = useTranslation('absences2')
  const { rowSelection } = props
  const { isExtraLarge } = useScreenResolution()
  const form = useFormContext<AbsencesPlanFormPayload>()

  const getLabelMessage = (record: StudentAttendance, isSelected: boolean) => {
    const statusTag = getAbsencesStatusTag(record.presence)
    return t(isSelected ? 'label.unselect-lesson' : 'label.select-lesson', {
      COURSE: record.lesson.course.displayName,
      DATE: formatDate(record.lesson.date),
      START_TIME: timeWithoutSeconds(record.lesson.startTime),
      END_TIME: timeWithoutSeconds(record.lesson.endTime),
      STATUS: statusTag.label
    })
  }

  const columns: Column<StudentAttendance>[] = [
    useNumberColumn(),
    {
      key: 'course',
      title: t('header.course'),
      dataIndex: ['lesson', 'course', 'displayName'],
      width: getColumnWidth(isExtraLarge, TABLE_CELL_WIDTH.XL)
    },
    {
      key: 'time-range',
      title: t('header.time-range'),
      render: (_, { lesson }) =>
        `${timeWithoutSeconds(lesson.startTime)} - ${timeWithoutSeconds(lesson.endTime)}`,
      width: getColumnWidth(isExtraLarge, TABLE_CELL_WIDTH.MD)
    },
    {
      key: 'date',
      title: t('header.date'),
      render: (_, record) => formatDate(record.lesson.date),
      width: getColumnWidth(isExtraLarge, TABLE_CELL_WIDTH.MD)
    },
    {
      key: 'status',
      title: t('header.status'),
      render: (_, record) => <Tag {...getAbsencesStatusTag(record.presence)} />,
      width: getColumnWidth(isExtraLarge, TABLE_CELL_WIDTH.MD)
    }
  ] as const

  const selection: RowSelection<StudentAttendance> = {
    selectedItems: rowSelection.selectedItems,
    allCheckboxValue: rowSelection.getSelectAllCheckboxValue(),
    count: props.count,
    labelHeader: selected =>
      t(selected ? 'label.unselect-all-lesson' : 'label.select-all-lesson'),
    labelItem: row =>
      getLabelMessage(row, rowSelection.selectedItems.includes(row.id)),
    onSelect: rowSelection.onSelect,
    onSelectAll: rowSelection.onSelectAll
  }

  const lessonValidationError = form.formState.errors.lessons

  return (
    <Table
      id="absence-plan-table"
      columns={columns}
      data={props.data}
      count={props.count}
      isLoading={props.isLoading}
      rowSelection={selection}
      rightTopBar={
        lessonValidationError ? (
          <div className={styles.errorContainer}>
            <WarningCircleIcon className={styles.errorIcon} />
            {lessonValidationError.message}
          </div>
        ) : null
      }
      caption={t('header.absence-plan')}
    />
  )
}

export default AbsencePlanTable
